import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiGet } from "../../utils/apiClient";

// GET /api/super/sync-health の返却型（判定は apps/api/src/services/syncHealth.ts に集約）

type SyncIssue = {
    mall: "rakuten" | "amazon" | "yahoo";
    kind: string;
    label: string;
    message: string;
    lastOkAt: string | null;
    sinceDays: number | null;
};

type SyncHealthRow = {
    tenantId: string;
    accountId: string;
    shopUrl: string;
    shopName: string;
    companyName: string;
    plan: string;
    trialStatus: string;
    dataFetch: boolean;
    /** RN-305：テストのお店か（会社一覧の契約内容が「テスト」）。既定で隠す */
    isTest?: boolean;
    monitored: boolean;
    enabledMalls: string[];
    issues: SyncIssue[];
};

// RN-275: 店舗をまたいだ楽天の取り込みの注意（apps/api/src/services/rakutenQuality.ts の QualityAlert）
type QualityAlert = {
    kind: "missing_field" | "became_zero" | "column_gone" | "became_empty" | "no_rows" | "no_rows_all";
    type: string;
    fields: { field: string; shopCount: number }[];
    shopCount: number;
    checkedShopCount: number;
    shopUrls: string[];
    /** 画面と通知に出す1行（サーバーで作った同じ文） */
    text: string;
};

type SyncHealthResponse = {
    ok: boolean;
    generatedAt: string;
    staleDays: number;
    reauthWarnDays: number;
    warnings: string[];
    qualityAlerts?: QualityAlert[];
    qualityMinShops?: number;
    kindLabels: Record<string, string>;
    summary: {
        totalShops: number;
        monitoredShops: number;
        issueShopCount: number;
        monitoredIssueShopCount: number;
        byKind: Record<string, number>;
    };
    /** RN-305：テストのお店も含めた数（「テスト店舗を表示」を入れたとき）。summary はテストを除いた数 */
    summaryWithTest?: SyncHealthResponse["summary"];
    testShops?: number;
    rows: SyncHealthRow[];
};

type IssueLine = {
    row: SyncHealthRow;
    issue: SyncIssue;
};

const MALL_LABELS: Record<string, string> = {
    rakuten: "楽天",
    amazon: "Amazon",
    yahoo: "Yahoo!",
};

// 認証系 > 同期エラー > 未更新 の順で並べる（通知と同じ優先度）
const KIND_ORDER: Record<string, number> = {
    rakuten_auth: 0,
    yahoo_reauth: 1,
    rakuten_error: 2,
    rakuten_quality: 2.5,
    amazon_sync_error: 3,
    yahoo_sync_error: 4,
    rakuten_stale: 5,
    amazon_stale: 6,
    yahoo_stale: 7,
};

// 憲法 1.1：エラーの種類ごとの説明（「エラーの種類詳細」の小窓）。
// 出る条件は apps/api/src/services/syncHealth.ts、消える条件は各取得の書き込みで確かめた（2026-10-20）
type KindHelp = {
    when: string;
    clears: string;
    needsAction: "要対応" | "様子見（続けば要対応）";
    who: string;
    fix: string;
};

const KIND_HELP: Record<string, KindHelp> = {
    rakuten_auth: {
        when: "楽天RMSにログインできず、そのお店のデータの取得を止めているとき（R-Login のパスワードの誤り・受注データのダウンロード用の ID の誤りなど）",
        clears: "取得では消えません。お客様が登録し直す、または運営が会社一覧で「解除」を押すと消えます（内容が正しいかは次の取得で分かります）",
        needsAction: "要対応",
        who: "お客様（パスワードを登録し直す）／運営（お客様への連絡・確認）",
        fix: "お客様が初期設定の「RMSログイン設定」で正しい内容を登録し直すと、次の取得から再開します",
    },
    rakuten_error: {
        when: "楽天からのデータの取得に失敗したとき（楽天側の一時的な不具合・ログインの途中のセキュリティエラー・画面の読み込みの失敗・設定の不足など）。パスワードの誤りのときは「RMS認証エラー」に出ます",
        clears: "そのあとの取得が1回成功すれば、自動で消えます。運営が会社一覧で「対応済み」を押しても消えます",
        needsAction: "様子見（続けば要対応）",
        who: "運営",
        fix: "1日たっても消えない・同じ文のエラーが何店舗にも出ているときは、楽天の画面が変わった疑いがあるので開発に連絡します",
    },
    rakuten_quality: {
        when: "楽天から取ったデータを、利益Navi に保存できなかったとき。または、R-Mail（メルマガの配信の実績）を楽天から取れなかった日があったとき。どちらも取得は「成功」と出ます",
        clears: "20時間たつと消えます（直っていなくても消えます）。次の日も出ていれば、まだ直っていません",
        needsAction: "様子見（続けば要対応）",
        who: "運営（開発に連絡）",
        fix: "翌日も出ているときは、開発に連絡します",
    },
    rakuten_stale: {
        when: "楽天のデータが2日以上新しく取れていないとき（取れたことが一度もないお店を含む）。RMS認証エラー・RMS同期エラーが出ている間は、そちらだけが出ます",
        clears: "取得が成功すれば、自動で消えます",
        needsAction: "要対応",
        who: "運営",
        fix: "初期設定が終わっているか（ID・パスワードが入っているか）を会社一覧で確かめ、取得が動いているかを開発に確かめます",
    },
    amazon_sync_error: {
        when: "Amazon のデータの取得（同期）が失敗したとき。（連続N回）は続けて失敗した回数です",
        clears: "次の同期で成功すれば、自動で消えます",
        needsAction: "様子見（続けば要対応）",
        who: "運営（連携が切れているときはお客様に連携のやり直しをお願いする）",
        fix: "何回も続くときは、文の中身を見て開発に連絡します（「refresh_token が…見つかりません」は、連携の鍵が無い＝お客様に連携のやり直しをお願いする）",
    },
    amazon_stale: {
        when: "Amazon のデータが2日以上新しく取れていないとき",
        clears: "同期が成功すれば、自動で消えます",
        needsAction: "要対応",
        who: "運営",
        fix: "Amazon の連携が有効か、同期が動いているかを確かめます",
    },
    yahoo_sync_error: {
        when: "Yahoo! のデータの取得（同期）が失敗したとき。（連続N回）は続けて失敗した回数です",
        clears: "次の同期で成功すれば、自動で消えます",
        needsAction: "様子見（続けば要対応）",
        who: "運営",
        fix: "何回も続くときは、文の中身を見て開発に連絡します",
    },
    yahoo_stale: {
        when: "Yahoo! のデータが2日以上新しく取れていないとき",
        clears: "同期が成功すれば、自動で消えます",
        needsAction: "要対応",
        who: "運営",
        fix: "Yahoo! の連携が有効か、同期が動いているかを確かめます",
    },
    yahoo_reauth: {
        when: "Yahoo! の連携の期限（連携してから4週間）が7日以内に切れる、またはもう切れているとき。切れると注文データの取得が止まります",
        clears: "連携をやり直すと消えます（自動では消えません）",
        needsAction: "要対応",
        who: "お客様（お客様が Yahoo! の画面を開くと、上に同じ警告が出ます）／運営（お客様への連絡）",
        fix: "「Yahoo連携設定」の画面から、もう一度「許可」を押して連携し直します",
    },
};

function kindBadgeClass(kind: string) {
    if (kind === "rakuten_auth" || kind === "yahoo_reauth") return "bg-red-100 text-red-800 border-red-200";
    if (kind.endsWith("_error") || kind === "rakuten_quality") return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
}

// 項目・列の一覧の見出し（apps/api/src/services/rakutenQuality.ts の fieldsLabel と同じ言葉）
function qualityFieldsLabel(kind: QualityAlert["kind"]): string {
    if (kind === "missing_field") return "見つからなかった欄";
    if (kind === "column_gone") return "なくなった列";
    if (kind === "became_empty") return "0か空になった項目";
    return "";
}

// 手元（localhost）だけで、本番と同じ形の「取り込みの注意」を見本として出す（見た目の確かめ用）。
// サーバー・データには何も書かない。本番・stg ではボタンを出さない
const IS_LOCAL = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);

function buildSampleQualityAlerts(rows: SyncHealthRow[]): QualityAlert[] {
    const pick = rows.filter((r) => r.monitored).concat(rows.filter((r) => !r.monitored));
    const urls = Array.from(new Set(pick.map((r) => r.shopUrl))).slice(0, 3);
    while (urls.length < 3) urls.push(`sample-shop-${urls.length + 1}`);
    const fields = [
        "CPC(クリック単価)",
        "CPM(インプレッション単価)",
        "ROAS",
        "キャンペーンタイプ",
        "入札戦略",
        "クリック率",
        "注文獲得単価",
        "有効予算",
        "消化率",
        "購入単価",
    ];
    return [
        {
            kind: "missing_field",
            type: "tdaexp",
            fields: fields.map((field) => ({ field, shopCount: 3 })),
            shopCount: 3,
            checkedShopCount: 3,
            shopUrls: urls,
            text: "楽天のTDA-EXP広告：10個の欄が見つかりませんでした（3店舗で発生）",
        },
        {
            kind: "column_gone",
            type: "tdaexp",
            fields: ["売上金額", "売上件数", "ROAS"].map((field) => ({ field, shopCount: 3 })),
            shopCount: 3,
            checkedShopCount: 3,
            shopUrls: urls,
            text: "楽天のTDA-EXP広告：前回まであった3個の列が、今回のファイルにありませんでした（3店舗で発生）",
        },
        {
            kind: "became_empty",
            type: "rpp",
            fields: ["売上金額", "売上件数"].map((field) => ({ field, shopCount: 4 })),
            shopCount: 4,
            checkedShopCount: 5,
            shopUrls: urls,
            text: "楽天のRPP広告：前回まで入っていた2個の項目が、今回は全部0か空になりました（4店舗で発生）",
        },
        {
            kind: "no_rows",
            type: "cpa",
            fields: [],
            shopCount: 3,
            checkedShopCount: 3,
            shopUrls: urls,
            text: "楽天のCPA広告：前回はデータがあったのに、今回は1行もありませんでした（3店舗で発生）",
        },
        {
            kind: "no_rows_all",
            type: "rmail",
            fields: [],
            shopCount: 4,
            checkedShopCount: 40,
            shopUrls: urls,
            text: "楽天のR-Mail：配信の実績が、どのお店にも1件も入りませんでした（最近まで入っていた4店舗を含む）",
        },
    ];
}

export default function SuperSyncHealthPage() {
    const [rawData, setData] = useState<SyncHealthResponse | null>(null);
    // 手元だけ：見本の「取り込みの注意」を出すか
    const [showSample, setShowSample] = useState(false);
    const data = useMemo<SyncHealthResponse | null>(() => {
        if (!rawData || !showSample) return rawData;
        return { ...rawData, qualityAlerts: buildSampleQualityAlerts(rawData.rows) };
    }, [rawData, showSample]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [mallFilter, setMallFilter] = useState("");
    const [kindFilter, setKindFilter] = useState("");
    const [monitoredOnly, setMonitoredOnly] = useState(false);
    // RN-305：テストのお店も表示するか。保存しない＝開くたびに隠れた状態から始まる（毎回隠す手間を作らない）
    const [showTest, setShowTest] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await apiGet("/api/super/sync-health");
            const json = (await res.json()) as SyncHealthResponse;
            if (!json?.ok) throw new Error((json as any)?.error || "取得に失敗しました");
            setData(json);
        } catch (e: any) {
            setError(e?.message ?? "取得に失敗しました");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const allLines = useMemo<IssueLine[]>(() => {
        if (!data) return [];
        const lines: IssueLine[] = [];
        for (const row of data.rows) {
            for (const issue of row.issues) {
                lines.push({ row, issue });
            }
        }
        lines.sort(
            (a, b) =>
                (KIND_ORDER[a.issue.kind] ?? 99) - (KIND_ORDER[b.issue.kind] ?? 99) ||
                a.row.shopUrl.localeCompare(b.row.shopUrl)
        );
        return lines;
    }, [data]);

    const filteredLines = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allLines.filter((l) => {
            if (!showTest && l.row.isTest) return false;
            if (monitoredOnly && !l.row.monitored) return false;
            if (mallFilter && l.issue.mall !== mallFilter) return false;
            if (kindFilter && l.issue.kind !== kindFilter) return false;
            if (q) {
                const hay = [l.row.companyName, l.row.shopName, l.row.shopUrl, l.row.plan, l.issue.message]
                    .join(" ")
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [allLines, showTest, monitoredOnly, mallFilter, kindFilter, search]);

    // RN-305：上の札の数も、表と同じくテストのお店を除いた数（入れたときだけ全部）
    const summary = data ? (showTest && data.summaryWithTest ? data.summaryWithTest : data.summary) : null;

    const kindOptions = useMemo(() => {
        if (!data) return [];
        return Object.entries(data.kindLabels).sort(
            ([a], [b]) => (KIND_ORDER[a] ?? 99) - (KIND_ORDER[b] ?? 99)
        );
    }, [data]);

    const label = "text-[11px] text-gray-500 leading-tight";
    const input = "border rounded px-2 py-1 text-xs leading-tight";
    const btn = "text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50";
    const th = "border px-2 py-1 text-center text-[12px] font-semibold whitespace-nowrap";
    const td = "border px-2 py-1 text-[12px] leading-tight";

    return (
        <div className="h-[calc(100vh-64px)] overflow-y-auto p-2">
            <div className="space-y-2 pb-16">
                {/* Header（憲法 1.1：何の画面か・何が並ぶかを、誰が見ても分かる言葉で） */}
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-bold text-gray-900">データ同期ヘルス</h1>
                            {/* 憲法 1.1：分からなければ押せば分かる。再読み込みと並べず、見出しの隣に置く */}
                            <button
                                onClick={() => setHelpOpen(true)}
                                className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-blue-300 bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100"
                            >
                                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white">?</span>
                                エラーの種類詳細
                            </button>
                        </div>
                        <div className="mt-1 text-[13px] text-gray-700 leading-relaxed">
                            お店ごとに、楽天・Amazon・Yahoo! のデータが毎日きちんと取れているかを確かめる画面です。
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {data && (
                            <div className="text-[11px] text-gray-500">
                                取得: {dayjs(data.generatedAt).format("YYYY/MM/DD HH:mm")}
                            </div>
                        )}
                        {IS_LOCAL && (
                            <button
                                onClick={() => setShowSample((v) => !v)}
                                className="text-xs px-2 py-1 border border-dashed border-purple-400 rounded bg-purple-50 text-purple-800 hover:bg-purple-100"
                                title="手元（localhost）だけに出るボタンです。本番と同じ形の「取り込みの注意」を見本として出します。データには何も書きません"
                            >
                                {showSample ? "見本を消す（手元だけ）" : "見本を表示（手元だけ）"}
                            </button>
                        )}
                        <button onClick={load} className={btn} disabled={loading}>
                            再読み込み
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
                )}

                {data && data.warnings.length > 0 && (
                    <div className="text-[11px] text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                        チェック中の警告: {data.warnings.join(" / ")}
                    </div>
                )}

                {/* Summary：行ごとに「何の数か」を頭に出す */}
                {data && summary && (
                    <div className="space-y-1.5 text-[11px]">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="w-24 shrink-0 text-[12px] font-semibold text-gray-600">店舗</span>
                            <span className="px-2 py-0.5 rounded border bg-white">
                                全店舗: <b>{summary.totalShops}</b>
                            </span>
                            <span className="px-2 py-0.5 rounded border bg-white">
                                監視対象（有料+トライアル）: <b>{summary.monitoredShops}</b>
                            </span>
                            <span className="px-2 py-0.5 rounded border bg-white">
                                問題あり店舗: <b>{summary.issueShopCount}</b>
                                （監視対象 <b>{summary.monitoredIssueShopCount}</b>）
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="w-24 shrink-0 text-[12px] font-semibold text-gray-600">エラー種別</span>
                            {kindOptions.every(([kind]) => (summary.byKind[kind] ?? 0) === 0) && (
                                <span className="text-gray-500">エラーのあるお店はありません</span>
                            )}
                            {kindOptions.map(([kind, kindLabel]) => {
                                const count = summary.byKind[kind] ?? 0;
                                if (count === 0) return null;
                                return (
                                    <button
                                        key={kind}
                                        onClick={() => setKindFilter((prev) => (prev === kind ? "" : kind))}
                                        className={`px-2 py-0.5 rounded border ${kindBadgeClass(kind)} ${kindFilter === kind ? "ring-2 ring-blue-300" : ""}`}
                                        title={`${KIND_HELP[kind]?.when ?? ""}（押すと、この種類だけに絞り込み）`}
                                    >
                                        {kindLabel}: <b>{count}</b>
                                    </button>
                                );
                            })}
                        </div>
                        {/* RN-275: 取り込みの点検（「取れた」と出ているのに数字が入っていないか）。平常時は灰色の札1つだけ */}
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="w-24 shrink-0 text-[12px] font-semibold text-gray-600">取り込みの点検</span>
                            {(data.qualityAlerts?.length ?? 0) > 0 ? (
                                <span className="px-2 py-0.5 rounded border bg-orange-100 text-orange-900 border-orange-300 font-semibold">
                                    注意: <b>{data.qualityAlerts?.length}</b>件（下に中身）
                                </span>
                            ) : (
                                <span
                                    className="px-2 py-0.5 rounded border bg-white text-gray-500"
                                    title={`前回まで取れていた列や数字が、いきなり無くなる・0や空になることが、同じ帳票で${data.qualityMinShops ?? 3}店舗以上で同時に起きると「注意」に変わります（有料・トライアルのお店の毎日の取得を見ています）`}
                                >
                                    問題なし
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* RN-275: 取り込みの注意の中身（問題があるときだけ） */}
                {data && (data.qualityAlerts?.length ?? 0) > 0 && (
                    <div className="text-[12px] text-orange-900 bg-orange-50 border border-orange-300 rounded px-2 py-1.5 space-y-0.5">
                        {/* 楽天側の変更の目印（お店ごとの問題ではない）。閉じた状態は1行だけ・中身は全部「詳しく」の中 */}
                        <details className="group">
                            <summary className="cursor-pointer list-none flex flex-wrap items-center gap-x-2">
                                <span className="font-semibold">
                                    ⚠ 楽天の取り込みで、前回までと違うことが複数のお店で同時に起きています（{(data.qualityAlerts ?? []).length}件）
                                </span>
                                <span className="text-[11px] text-orange-700 underline group-open:hidden">詳しく ▼</span>
                                <span className="text-[11px] text-orange-700 underline hidden group-open:inline">閉じる ▲</span>
                            </summary>
                            <div className="mt-1 space-y-1">
                                {(data.qualityAlerts ?? []).map((a, i) => (
                                    <div key={`${a.kind}-${a.type}-${i}`}>
                                        <div>・{a.text}</div>
                                        {a.fields.length > 0 && qualityFieldsLabel(a.kind) && (
                                            <div className="ml-3 text-[11px] text-orange-900/90">
                                                {qualityFieldsLabel(a.kind)}：{a.fields.map((f) => `「${f.field}」`).join("")}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div className="text-[11px] text-orange-800/80">
                                    楽天側で帳票が変わった疑いがあります。同じ帳票を使うお店すべてに影響している可能性があるので、開発に連絡してください。
                                </div>
                            </div>
                        </details>
                    </div>
                )}

                {/* Filters */}
                <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col">
                        <label className={label}>検索（会社名/店舗/内容）</label>
                        <input
                            className={`${input} w-56`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="例: 会社名"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className={label}>モール</label>
                        <select className={input} value={mallFilter} onChange={(e) => setMallFilter(e.target.value)}>
                            <option value="">(all)</option>
                            <option value="rakuten">楽天</option>
                            <option value="amazon">Amazon</option>
                            <option value="yahoo">Yahoo!</option>
                        </select>
                    </div>
                    <div className="flex flex-col">
                        <label className={label}>種別</label>
                        <select className={input} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
                            <option value="">(all)</option>
                            {kindOptions.map(([kind, kindLabel]) => (
                                <option key={kind} value={kind}>
                                    {kindLabel}
                                </option>
                            ))}
                        </select>
                    </div>
                    <label className="flex items-center gap-1 text-xs pb-1">
                        <input
                            type="checkbox"
                            checked={monitoredOnly}
                            onChange={(e) => setMonitoredOnly(e.target.checked)}
                        />
                        監視対象のみ（有料+トライアル）
                    </label>
                    {/* RN-305：テストのお店は既定で隠す（札の数にも入れない） */}
                    <label
                        className="flex items-center gap-1 text-xs pb-1"
                        title="会社一覧で契約内容が「テスト」のお店は、ふだんは表にも札の数にも入れていません（13:30 の通知にも入りません）。入れると表示します（ページを開き直すと隠れた状態に戻ります）"
                    >
                        <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} />
                        テスト店舗を表示{data?.testShops != null ? `（${data.testShops}店舗）` : ""}
                    </label>
                    <div className="ml-auto text-[11px] text-gray-500 pb-1">
                        表示: <b>{filteredLines.length}</b> 件
                    </div>
                </div>

                {/* Table */}
                <div className="rounded border overflow-x-auto bg-white">
                    <table className="w-full border-collapse">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className={`${th} w-40`}>会社名</th>
                                <th className={`${th} w-32`}>shopUrl</th>
                                <th className={`${th} w-28`}>プラン</th>
                                <th className={`${th} w-24`}>契約ステータス</th>
                                <th className={`${th} w-16`}>モール</th>
                                <th className={`${th} w-32`}>種別</th>
                                <th className={th}>内容</th>
                                <th className={`${th} w-24`}>最終成功</th>
                                <th className={`${th} w-16`}>経過</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td className="px-2 py-2 text-xs text-gray-500" colSpan={9}>
                                        Loading...
                                    </td>
                                </tr>
                            ) : filteredLines.length === 0 ? (
                                <tr>
                                    <td className="px-2 py-2 text-xs text-gray-500" colSpan={9}>
                                        {/* RN-305：隠しているテストのお店の行は数えない（札の数と同じ） */}
                                        {allLines.every((l) => !showTest && l.row.isTest) ? "問題のある店舗はありません 🎉" : "条件に一致する項目がありません"}
                                    </td>
                                </tr>
                            ) : (
                                filteredLines.map((l, idx) => {
                                    const name = l.row.companyName || l.row.shopName || "-";
                                    return (
                                        <tr key={`${l.row.tenantId}-${l.row.shopUrl}-${l.issue.mall}-${l.issue.kind}-${idx}`} className="hover:bg-gray-50">
                                            <td className={`${td} text-left truncate max-w-[10rem]`} title={name}>
                                                {name}
                                                {!l.row.monitored && (
                                                    <span className="ml-1 text-[10px] text-gray-400">(対象外)</span>
                                                )}
                                            </td>
                                            <td className={`${td} text-left truncate max-w-[8rem]`} title={l.row.shopUrl}>
                                                {l.row.shopUrl}
                                            </td>
                                            <td className={`${td} text-left whitespace-nowrap`}>{l.row.plan || "-"}</td>
                                            <td className={`${td} text-left whitespace-nowrap`}>{l.row.trialStatus || "-"}</td>
                                            <td className={`${td} text-center whitespace-nowrap`}>
                                                {MALL_LABELS[l.issue.mall] ?? l.issue.mall}
                                            </td>
                                            <td className={`${td} text-center whitespace-nowrap`}>
                                                <span
                                                    className={`inline-block px-1.5 py-0.5 rounded border text-[10px] leading-none ${kindBadgeClass(l.issue.kind)}`}
                                                >
                                                    {l.issue.label}
                                                </span>
                                            </td>
                                            <td className={`${td} text-left`} title={l.issue.message}>
                                                {l.issue.message}
                                            </td>
                                            <td className={`${td} text-center whitespace-nowrap`}>
                                                {l.issue.lastOkAt ? dayjs(l.issue.lastOkAt).format("YYYY/MM/DD") : "-"}
                                            </td>
                                            <td className={`${td} text-center whitespace-nowrap`}>
                                                {l.issue.sinceDays != null ? `${l.issue.sinceDays}日` : "-"}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="text-[11px] text-gray-500 leading-tight">
                    ※ 日次通知（Chatwork）の対象は「監視対象（有料+トライアル）」のみ。無料層を含む全店舗の棚卸しはこの画面で行う /
                    RMS認証エラーの解決操作は会社一覧の「対応済みにする」から
                </div>
            </div>

            {/* エラーの種類詳細（憲法 1.1）。①は画面の「エラー種別」の札・一覧の「種別」の列と同じ名前（サーバーの kindLabels）で、全種類を出す */}
            {helpOpen && data && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setHelpOpen(false)} />
                    <div className="relative w-[1000px] max-w-[95vw] max-h-[88vh] flex flex-col rounded-lg bg-white shadow-lg border p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="text-base font-bold text-gray-900">エラーの種類詳細</div>
                            <button className={btn} onClick={() => setHelpOpen(false)}>
                                閉じる
                            </button>
                        </div>
                        <div className="mt-3 overflow-auto space-y-4">
                            <section>
                                <div className="text-[13px] font-bold text-gray-800">① エラー種別</div>
                                <div className="mt-0.5 text-[12px] text-gray-600">
                                    上の「エラー種別」の札と、一覧の「種別」の列に出るものです。お店ごとに1行ずつ出ます。
                                </div>
                                <table className="mt-2 w-full text-[12px] border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-700">
                                            <th className={th}>種別</th>
                                            <th className={th}>どんなときに出るか</th>
                                            <th className={th}>自動で消えるか</th>
                                            <th className={th}>誰が対応するか</th>
                                            <th className={th}>どうすれば直るか</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {kindOptions.map(([kind, kindLabel]) => {
                                            const h = KIND_HELP[kind];
                                            return (
                                                <tr key={kind} className="align-top">
                                                    <td className={`${td} whitespace-nowrap`}>
                                                        <span className={`inline-block px-1.5 py-0.5 rounded border text-[11px] ${kindBadgeClass(kind)}`}>
                                                            {kindLabel}
                                                        </span>
                                                        {h && (
                                                            <div
                                                                className={`mt-1 text-[11px] font-semibold ${
                                                                    h.needsAction === "要対応" ? "text-red-700" : "text-gray-600"
                                                                }`}
                                                            >
                                                                {h.needsAction}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className={td}>{h?.when ?? "-"}</td>
                                                    <td className={td}>{h?.clears ?? "-"}</td>
                                                    <td className={td}>{h?.who ?? "-"}</td>
                                                    <td className={td}>{h?.fix ?? "-"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </section>

                            <section>
                                <div className="text-[13px] font-bold text-gray-800">② 取り込みの点検</div>
                                <div className="mt-0.5 text-[12px] text-gray-600">
                                    上の「取り込みの点検」の札の説明です。楽天側で帳票が変わったことに気づくための目印です（お店ごとの問題ではないので、下の一覧には出ません）。
                                </div>
                                <table className="mt-2 w-full text-[12px] border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-700">
                                            <th className={th}>表示</th>
                                            <th className={th}>どんなときに出るか</th>
                                            <th className={th}>自動で消えるか</th>
                                            <th className={th}>誰が対応するか</th>
                                            <th className={th}>どうすれば直るか</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="align-top">
                                            <td className={`${td} whitespace-nowrap`}>
                                                <span className="inline-block px-1.5 py-0.5 rounded border text-[11px] bg-white text-gray-500">問題なし</span>
                                            </td>
                                            <td className={td}>ふだんの表示です</td>
                                            <td className={td}>-</td>
                                            <td className={td}>-</td>
                                            <td className={td}>何もしなくて大丈夫です</td>
                                        </tr>
                                        <tr className="align-top">
                                            <td className={`${td} whitespace-nowrap`}>
                                                <span className="inline-block px-1.5 py-0.5 rounded border text-[11px] bg-orange-100 text-orange-900 border-orange-300">
                                                    注意: N件
                                                </span>
                                                <div className="mt-1 text-[11px] font-semibold text-red-700">要対応</div>
                                            </td>
                                            <td className={td}>
                                                同じ帳票（RPP広告など）で、前回まで取れていたデータが急に取れない・0や空になったことが、
                                                有料・トライアルのお店の{data.qualityMinShops ?? 3}店舗以上で同時に起きたとき。中身は札の下のオレンジの枠の「詳しく」に出ます
                                            </td>
                                            <td className={td}>
                                                「欄が見つからない」は直すまで毎日出ます。「R-Mail がどのお店にも入らない」は、最後に入ってから30日のあいだ毎日出ます。ほかは前の日と比べて出すので、起きた日だけ出ます（出た日に対応します）
                                            </td>
                                            <td className={td}>運営（開発に連絡）</td>
                                            <td className={td}>
                                                開発に連絡します（開発が楽天のファイルの実物と突き合わせて、取り込みを直します）
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
